import { useEffect, useRef } from 'react';
import { fetchData } from '../api/api';
import { useAssistant } from '../components/Assistant/AssistantContext';
import { useModal } from '../context/ModalContext';
import { handleError } from '../context/errors/errorUtils';

const HINT_COOLDOWN_MS = 60000;
const FIRST_HINT_COOLDOWN_MS = 4000;
const HINT_POLL_INTERVAL_MS = 5000;
const POST_FIRST_COOLDOWN_MS = 60000;
const PATH_LEARN_FIRST_HINT_DELAY_MS = 20000;
const FUNCTIONAL_HINT_SCREENS = new Set([
  'home',
  'pathLearn',
]);

let lastFunctionalHintTime = 0;
let lastGrammarHintTime = 0;
let lastFunctionalHintType = '';
let lastFirstHintTime = 0;
let firstFlowStarted = false;
let postFirstCooldownUntil = 0;

const functionalTipsRef = { current: [] };
const grammarTipsRef = { current: [] };
const isFunctionalFetchingRef = { current: false };
const isGrammarFetchingRef = { current: false };
const isFunctionalHandlingRef = { current: false };
const functionalScreenStateRef = {
  current: {
    screen: null,
    enteredAt: 0,
    firstHintShown: false,
  },
};

const getFunctionalTrigger = (screen) => {
  if (screen === 'pathLearn') return 'learn';
  return null;
};

export const getAssistantHints = async (
  screen,
  userId,
  language,
  targetLanguage,
  serverSettings,
  actions,
  learnedWordsCount = 0,
  hasOpenModals = false,
) => {
  if (!userId || !language || !screen || !serverSettings || !actions) {
    /*console.log('[AssistantHints] Early return: missing required params', {
      userId: !!userId, language: !!language, screen: !!screen, 
      serverSettings: !!serverSettings, actions: !!actions
    });*/
    return;
  }
  if (!FUNCTIONAL_HINT_SCREENS.has(screen)) {
    //console.log('[AssistantHints] Screen not in FUNCTIONAL_HINT_SCREENS:', screen);
    return;
  }
  
  // Don't show hints if page is hidden or window is not focused
  if (typeof document !== 'undefined' && document.hidden) {
    //console.log('[AssistantHints] Page hidden - skipping hints');
    return;
  }
  if (typeof window !== 'undefined' && !window.document.hasFocus?.()) {
    //console.log('[AssistantHints] Window not focused - skipping hints');
    return;
  }
  
  // Don't show hints if there are open modals (like Logo video player)
  if (hasOpenModals) {
    //console.log('[AssistantHints] Open modals detected - skipping hints');
    return;
  }
  
  if (isFunctionalHandlingRef.current) {
    //console.log('[AssistantHints] Already handling hint - skipping');
    return;
  }

  isFunctionalHandlingRef.current = true;

  try {
    const now = Date.now();

    if (functionalScreenStateRef.current.screen !== screen) {
      functionalScreenStateRef.current = {
        screen,
        enteredAt: now,
        firstHintShown: false,
      };
      functionalTipsRef.current = [];
    }

    if (
      screen === 'pathLearn'
      && !functionalScreenStateRef.current.firstHintShown
      && now - functionalScreenStateRef.current.enteredAt < PATH_LEARN_FIRST_HINT_DELAY_MS
    ) {
      /*console.log('[AssistantHints] pathLearn: waiting before first hint', {
        waitedSoFar: now - functionalScreenStateRef.current.enteredAt,
        required: PATH_LEARN_FIRST_HINT_DELAY_MS
      });*/
      return;
    }

    if (postFirstCooldownUntil > now) {
      //console.log('[AssistantHints] Still in postFirstCooldown - skipping');
      return;
    }

    const activeCooldown = lastFunctionalHintType === 'first'
      ? FIRST_HINT_COOLDOWN_MS
      : HINT_COOLDOWN_MS;
    const timeSinceLastHint = now - lastFunctionalHintTime;

    if (timeSinceLastHint < activeCooldown) {
      /*console.log('[AssistantHints] Cooldown not reached:', {
        timeSinceLastHint,
        activeCooldown,
        remaining: activeCooldown - timeSinceLastHint
      });*/
      return;
    }

    const fetchTips = async () => {
      if (isFunctionalFetchingRef.current) return;
      isFunctionalFetchingRef.current = true;

      try {
        const response = await fetchData('/api/tips/functional', 'POST', {
          userId,
          language,
          targetLanguage,
          screen,
          trigger: getFunctionalTrigger(screen),
          learnedWordsCount,
        });
        const newTips = response?.tips || [];
        if (Array.isArray(newTips) && newTips.length > 0) {
          functionalTipsRef.current = newTips;
        }
      } catch (error) {
        handleError(error, {
          module: 'assistant_hint',
          action: 'fetch_functional_tips',
          screen,
          userId,
          language,
          targetLanguage,
        });
      } finally {
        isFunctionalFetchingRef.current = false;
      }
    };

    if (functionalTipsRef.current.length === 0) {
      //console.log('[AssistantHints] No tips in queue - fetching new tips');
      await fetchTips();
      if (functionalTipsRef.current.length === 0) {
        //console.log('[AssistantHints] No tips available after fetch - skipping');
        return;
      }
    }

    const nextTip = functionalTipsRef.current[0];
    const nextTipType = String(nextTip?.type || '').trim().toLowerCase();
    const nextTipIsFirst = nextTipType === 'first' || Boolean(nextTip?.isFirstFunctionalTip);
    if (!nextTipIsFirst && firstFlowStarted) {
      const requiredPauseUntil = lastFirstHintTime + POST_FIRST_COOLDOWN_MS;
      if (now < requiredPauseUntil) {
        postFirstCooldownUntil = requiredPauseUntil;
        //console.log('[AssistantHints] Post-first cooldown active - skipping');
        return;
      }
      firstFlowStarted = false;
    }

    const currentTip = functionalTipsRef.current.shift();
    const text = currentTip?.text || '';
    const audioSrc = currentTip?.audioSrc || currentTip?.audio || currentTip?.audioUrl || null;
    const tipType = String(currentTip?.type || '').trim().toLowerCase();
    const isFirstTip = tipType === 'first' || Boolean(currentTip?.isFirstFunctionalTip);
    const variant = isFirstTip ? 'sticky' : 'ephemeral';

    if (!text) {
      //console.log('[AssistantHints] No text in tip - skipping');
      return;
    }

    /*console.log('[AssistantHints] 🎯 Showing hint:', {
      screen,
      type: tipType,
      variant,
      hasAudio: !!audioSrc,
      textPreview: text.substring(0, 50) + '...'
    });*/

    lastFunctionalHintTime = now;
    lastFunctionalHintType = tipType;
    functionalScreenStateRef.current.firstHintShown = true;
    if (isFirstTip) {
      firstFlowStarted = true;
      lastFirstHintTime = now;
      postFirstCooldownUntil = 0;
    }
    actions.resetState();
    await actions.speakText(text, { variant, audioSrc });
  } finally {
    isFunctionalHandlingRef.current = false;
  }
};

export const getAssistantGrammarHints = async ({
  userId,
  language,
  targetLanguage,
  actions,
  source = 'pathLessons',
}) => {
  if (!userId || !language || !targetLanguage || !actions) return;
  if (source !== 'pathLessons') return;

  const now = Date.now();
  const timeSinceLastHint = now - lastGrammarHintTime;

  if (timeSinceLastHint < HINT_COOLDOWN_MS) {
    return;
  }

  const fetchTips = async () => {
    if (isGrammarFetchingRef.current) return;
    isGrammarFetchingRef.current = true;

    try {
      const response = await fetchData('/api/tips/grammar', 'POST', {
        userId,
        language,
        targetLanguage,
        screen: source,
      });
      const newTips = response?.tips || [];
      if (Array.isArray(newTips) && newTips.length > 0) {
        grammarTipsRef.current = newTips;
      }
    } catch (error) {
      handleError(error, {
        module: 'assistant_hint',
        action: 'fetch_grammar_tips',
        screen: source,
        userId,
        language,
        targetLanguage,
      });
    } finally {
      isGrammarFetchingRef.current = false;
    }
  };

  if (grammarTipsRef.current.length === 0) {
    await fetchTips();
    if (grammarTipsRef.current.length === 0) return;
  }

  const currentTip = grammarTipsRef.current.shift();
  const text = currentTip?.text || '';
  const audioSrc = currentTip?.audioSrc || currentTip?.audio || currentTip?.audioUrl || null;
  if (!text) return;

  lastGrammarHintTime = now;
  actions.resetState();
  await actions.speakText(text, { variant: 'ephemeral', audioSrc });
};

export const useAssistantHints = (
  screen,
  userId,
  language,
  targetLanguage,
  serverSettings,
  learnedWordsCount,
) => {
  const { actions } = useAssistant();
  const { hasOpenModals } = useModal();
  const isPageFocusedRef = useRef(true);
  const isPageVisibleRef = useRef(!document.hidden);

  useEffect(() => {
    // Track window focus
    const handleFocus = () => {
      isPageFocusedRef.current = true;
      //console.log('[AssistantHints] Window focused');
    };
    const handleBlur = () => {
      isPageFocusedRef.current = false;
      //console.log('[AssistantHints] Window blurred');
    };

    // Track page visibility
    const handleVisibilityChange = () => {
      isPageVisibleRef.current = !document.hidden;
      //console.log('[AssistantHints] Page visibility changed:', !document.hidden ? 'visible' : 'hidden');
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    /*console.log('[AssistantHints] Hook mounted/updated:', {
      screen,
      userId: userId?.substring(0, 8) + '...',
      hasOpenModals,
      language,
      targetLanguage
    });
    */
    getAssistantHints(
      screen,
      userId,
      language,
      targetLanguage,
      serverSettings,
      actions,
      learnedWordsCount,
      hasOpenModals,
    );
    const intervalId = setInterval(() => {
      getAssistantHints(
        screen,
        userId,
        language,
        targetLanguage,
        serverSettings,
        actions,
        learnedWordsCount,
        hasOpenModals,
      );
    }, HINT_POLL_INTERVAL_MS);

    return () => {
      //console.log('[AssistantHints] Hook unmounting, clearing interval');
      clearInterval(intervalId);
    };
  }, [screen, userId, language, targetLanguage, serverSettings, actions, learnedWordsCount, hasOpenModals]);
};
